/**
 * useClipPlayback Hook
 *
 * Makes a clip behave like a standalone video inside a shared source file: playback starts at
 * the clip's start, stops at its end, and scrubbing is confined to the window in between.
 *
 * Pass the returned ref and handlers straight to a `<video>`. When the video isn't a clip
 * (either bound is null) every handler is a no-op, so callers don't need to branch.
 */

import { useCallback, useRef } from 'react';

export interface ClipPlaybackHandlers {
  onLoadedMetadata: () => void;
  onTimeUpdate: () => void;
  onSeeking: () => void;
}

export function useClipPlayback(
  clipStartSeconds?: number | null,
  clipEndSeconds?: number | null
): {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isClip: boolean;
  handlers: ClipPlaybackHandlers;
} {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isClip = clipStartSeconds != null && clipEndSeconds != null;

  // Land on the clip's first frame rather than the start of the source file.
  const onLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video || clipStartSeconds == null) return;
    video.currentTime = clipStartSeconds;
  }, [clipStartSeconds]);

  // The source file keeps running past the clip, so stop it at the boundary. `timeupdate`
  // fires roughly every 250ms, so a clip can overrun by a frame or two — acceptable, and much
  // cheaper than polling on an animation frame.
  const onTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video || clipEndSeconds == null) return;

    if (video.currentTime >= clipEndSeconds) {
      video.pause();
      // Park on the clip's start so pressing play again replays the clip rather than the
      // remainder of the source video.
      if (clipStartSeconds != null) {
        video.currentTime = clipStartSeconds;
      }
    }
  }, [clipEndSeconds, clipStartSeconds]);

  // Scrubbing outside the window is meaningless — that footage isn't part of this video.
  const onSeeking = useCallback(() => {
    const video = videoRef.current;
    if (!video || clipStartSeconds == null || clipEndSeconds == null) return;

    if (video.currentTime < clipStartSeconds) {
      video.currentTime = clipStartSeconds;
    } else if (video.currentTime > clipEndSeconds) {
      video.currentTime = clipEndSeconds;
    }
  }, [clipStartSeconds, clipEndSeconds]);

  return {
    videoRef,
    isClip,
    handlers: { onLoadedMetadata, onTimeUpdate, onSeeking },
  };
}
